import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";

/**
 * Every action the application records today, for the filter on the audit
 * screen. Grouped by the prefix before the dot, which is also how the UI
 * groups the options.
 *
 * Keep it in step with the `audit.record` call sites: an action missing here
 * is still written and still listed, it just cannot be filtered for.
 */
export const AUDIT_ACTIONS = [
  "auth.login",
  "auth.logout",
  "auth.logout_all",
  "organization.create",
  "organization.suspend",
  "organization.reactivate",
  "user.invite",
  "user.membership_granted",
  "user.update",
  "site.create",
  "asset.create",
  "asset.update",
  "device.create",
  "device.update",
  "device.deactivate",
  "incident.acknowledge",
  "alert_rule.create",
  "alert_rule.create_defaults",
  "alert_rule.update",
  "messaging_contact.create",
  "messaging_contact.revoke_consent",
  "messaging_contact.reinstate_consent",
  "notification.test_whatsapp",
  "platform_settings.update",
] as const;

export const auditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof auditActionSchema>;

export const AUDIT_RESOURCE_TYPES = [
  "Organization",
  "User",
  "Site",
  "Asset",
  "Device",
  "Incident",
  "AlertRule",
  "MessagingContact",
  "PlatformSettings",
] as const;

export const auditResourceTypeSchema = z.enum(AUDIT_RESOURCE_TYPES);
export type AuditResourceType = z.infer<typeof auditResourceTypeSchema>;

export const auditLogSchema = z.object({
  id: z.uuid(),
  /**
   * Null for a platform-level action that belongs to no tenant — a super
   * admin signing in, for instance. Only a super admin ever reads those rows.
   */
  organizationId: z.uuid().nullable(),
  organizationName: z.string().nullable(),
  /** Null once the actor is deleted; `actorEmail` survives them. */
  actorUserId: z.uuid().nullable(),
  actorEmail: z.string().nullable(),
  actorName: z.string().nullable(),
  /**
   * Deliberately a loose string rather than `auditActionSchema`.
   *
   * The trail is append-only and older than any list we keep in code. If an
   * action is renamed or retired, the historical rows must still read back
   * instead of failing validation and hiding the history — the same
   * asymmetry as the hardware registry in ADR-050: writes constrained, reads
   * not.
   */
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.uuid().nullable(),
  /**
   * Safe context only, and an object by construction: the write path types
   * it as a JSON object. Never passwords, tokens or full payloads.
   */
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.iso.datetime(),
});

export type AuditLog = z.infer<typeof auditLogSchema>;

export const listAuditLogsQuerySchema = listQuerySchema.extend({
  action: auditActionSchema.optional(),
  resourceType: auditResourceTypeSchema.optional(),
  /** Everything one person did. */
  actorUserId: z.uuid().optional(),
  /** The history of one device, asset or member. */
  resourceId: z.uuid().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export type ListAuditLogsQuery = z.infer<typeof listAuditLogsQuerySchema>;

export const auditLogListSchema = paginatedSchema(auditLogSchema);
export type AuditLogList = z.infer<typeof auditLogListSchema>;
